import React, { FC, useState } from 'react';
import { Close, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory, useParams } from 'react-router';
import { Page, PageTitleWithBack, Button as SMButton, Loader } from '../../../../../components';
import {
  getApplicationRoleDetails,
  createApplicationRole,
  updateApplicationRole
} from '../../../../../fetch';
import { IApplicationRoleDetails } from '../../../../../models';
import { NavigationAuthorization } from './navigation-authorization';

interface IAppRoleDetails {}

export const AppRoleDetails: FC<IAppRoleDetails> = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { roleId }: { roleId: string } = useParams();
  const classes = useStyles();
  const [isSaving, setSaving] = useState(false);
  const [value, setValue] = useState('');
  const [systemDisplayName, setSystemDisplayName] = useState('');
  const [systemDescription, setSystemDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);

  const isAddRole = roleId === 'add-role';

  const { isLoading: isLoadingRoles } = useQuery<IApplicationRoleDetails, Error>(
    ['getApplicationRoleDetails', roleId],
    () => getApplicationRoleDetails(Number(roleId)),
    {
      enabled: !isAddRole,
      notifyOnChangeProps: 'tracked',
      onSuccess: data => {
        setValue(data.value);
        setSystemDisplayName(data.systemDisplayName);
        setSystemDescription(data.systemDescription);
        setIsEnabled(data.isEnabled);
      }
    }
  );

  const title = isAddRole
    ? `Add a New Role`
    : systemDisplayName
    ? `Edit Role - ${systemDisplayName}`
    : 'Edit Role';
  return (
    <>
      <Page
        title="Role Details"
        customBreadcrumbPath={`/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles/${systemDisplayName}`}
      >
        <PageTitleWithBack
          backText="Back to Role list"
          backUrl="/admin/user-tools/unified-user-management/manage-roles-and-titles/app-roles"
          title={title}
        />
        <Box className={classes.content}>
          {isLoadingRoles && (
            <Box marginTop="16px">
              <Loader position="centered" type="inline" />
            </Box>
          )}
          {!isLoadingRoles && (
            <>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    label="Value"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    label="System Display Name"
                    value={systemDisplayName}
                    onChange={e => setSystemDisplayName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    label="System Description"
                    value={systemDescription}
                    onChange={e => setSystemDescription(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isEnabled}
                        onChange={e => setIsEnabled(e.target.checked)}
                        name="isEnabled"
                        color="primary"
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid container alignItems="center" className={classes.saveWrapper}>
                  <ButtonGroup>
                    <SMButton
                      id="save"
                      color="primary"
                      variant="contained"
                      disabled={!value || !systemDisplayName || !systemDescription || isSaving}
                      startIcon={<Save />}
                      onClick={async () => {
                        setSaving(true);
                        try {
                          if (roleId === 'add') {
                            const res = await createApplicationRole({
                              value,
                              displayName: systemDisplayName,
                              description: systemDescription,
                              isEnabled
                            });
                            enqueueSnackbar('Role created successfully!', {
                              variant: 'success'
                            });
                            history.push(
                              `/admin/user-tools/unified-user-management/manage-roles-and-titles/${res}`
                            );
                          } else {
                            await updateApplicationRole(roleId, {
                              value,
                              displayName: systemDisplayName,
                              description: systemDescription,
                              isEnabled
                            });
                            enqueueSnackbar('Role updated successfully!', {
                              variant: 'success'
                            });
                          }
                        } catch (error) {
                          enqueueSnackbar(error?.Detail || 'Error updating role', {
                            variant: 'error'
                          });
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      Save Role
                    </SMButton>
                    <Button
                      id="remove"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push(
                          '/admin/user-tools/unified-user-management/manage-roles-and-titles'
                        );
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                {!isAddRole && (
                  <Grid item xs={12}>
                    <NavigationAuthorization />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    marginTop: theme.spacing(0.5)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  }
}));
